import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { StyleSheet, View } from 'react-native';

import { spacing } from '@/constants/theme';
import { voltrUploadStore } from '@/stores/voltrUpload.store';
import { showsJob } from '@/stores/voltrUploadTypes';
import type { StepVideo } from '@/types/deal';
import type { VideoAdStats } from '@/types/voltr';

import { DisclosureToggle } from './DisclosureToggle';
import { SocialVideoCard } from './SocialVideoCard';

/** What the deal page passes down for the videos: stills, the two actions, the scroll registry. */
export type VideoHandlers = {
  thumbnails?: Record<string, string>;
  /** A video's ad numbers, once a brand runs it. */
  ads?: Record<string, VideoAdStats>;
  onReplace?: (video: StepVideo) => void;
  onRemove?: (video: StepVideo) => void;
  /** Videos being removed right now: their buttons wait. */
  removing?: ReadonlySet<string>;
  registerVideo?: (id: string, view: View | null) => void;
  /** The hero's latest "Fix N videos" (n counts presses): the step holding that video opens. */
  focusRequest?: { videoId: string; n: number } | null;
};

export type SocialStepVideosProps = VideoHandlers & {
  videos: StepVideo[];
  /** Replaced or removed: folded under "Earlier videos (N)", opened with See. */
  earlier?: StepVideo[];
  testID?: string;
};

/** The videos on the upload step, each on its own card, then the earlier ones folded away. */
export const SocialStepVideos = observer(function SocialStepVideos({ videos, earlier = [], thumbnails, ads, onReplace, onRemove, removing, registerVideo, testID }: SocialStepVideosProps) {
  const [showEarlier, setShowEarlier] = useState(false);
  // Several sent back: each card's Replace steps down so the page has one loud button (the hero's Fix N).
  const quiet = videos.filter((v) => v.sent_back).length > 1;
  const card = (video: StepVideo, actionable: boolean) => (
    <SocialVideoCard
      key={video.id}
      video={video}
      thumbnail={own(thumbnails, video.id)}
      ads={own(ads, video.id)}
      busy={removing?.has(video.id)}
      quiet={quiet}
      onReplace={actionable ? onReplace : undefined}
      onRemove={actionable ? onRemove : undefined}
      cardRef={actionable && registerVideo ? (view) => registerVideo(video.id, view) : undefined}
      testID={testID ? `${testID}-${video.id}` : undefined}
    />
  );

  return (
    <View style={styles.list} testID={testID}>
      {videos.filter((video) => !showsJob(voltrUploadStore.items, video.id)).map((video) => card(video, true))}
      {earlier.length > 0 ? (
        <View style={styles.earlier}>
          <DisclosureToggle
            open={showEarlier}
            label={`Earlier videos (${earlier.length})`}
            onPress={() => setShowEarlier(!showEarlier)}
            testID={testID ? `${testID}-earlier-toggle` : undefined}
          />
        </View>
      ) : null}
      {showEarlier ? earlier.map((video) => card(video, false)) : null}
    </View>
  );
});

/** A still or ad numbers by video id, own keys only: ids are server-sent, so never "constructor" off the prototype. */
function own<T>(byId: Record<string, T> | undefined, id: string): T | undefined {
  return byId && Object.prototype.hasOwnProperty.call(byId, id) ? byId[id] : undefined;
}

const styles = StyleSheet.create({
  list: { gap: spacing.sm, marginTop: spacing.xs },
  earlier: { alignItems: 'flex-start', paddingVertical: spacing.xs },
});
